import { useState } from "react";
import { imageSrc, srcSetFor } from "@/lib/imageSrc";
import { dimsOf, FALLBACK_AR } from "@/data/imageMeta";

/**
 * Responsive <picture> wrapper.
 *
 *   <ResponsiveArt
 *     src={url}
 *     alt="…"
 *     sizes="(min-width: 768px) 58vw, 100vw"
 *     eager={index === 0}
 *     className="..."
 *     onImg={({ ref, onLoad, ...attrs }) => <motion.img {...attrs} whileHover={...} />}
 *   />
 *
 * If `onImg` is omitted, a plain <img> is rendered. Either way the picture
 * emits AVIF + WebP <source>s with full srcset (5 widths), plus the JPG
 * fallback on the inner <img>.
 *
 * Width / height attributes are sourced from /data/imageMeta.js (pixel-accurate)
 * to reserve layout space before the image loads (zero CLS).
 */
export default function ResponsiveArt({
  src,
  alt = "",
  sizes,
  eager = false,
  className = "",
  pictureClassName = "",
  fadeIn = true,
  bg = "#E8E6DD",
  testid,
  onImg,
  imgStyle,
  ...rest
}) {
  const [loaded, setLoaded] = useState(false);
  const dims = dimsOf(src);
  const ar = dims ? dims.ar : FALLBACK_AR;

  // Pick a representative width/height for the <img> attrs — actual layout
  // is controlled by CSS, but native intrinsic ratio comes from these.
  const W = dims ? dims.w : 1600;
  const H = dims ? dims.h : Math.round(W / FALLBACK_AR);

  const imgAttrs = {
    src: imageSrc(src, 1200, "jpg"),
    srcSet: srcSetFor(src, "jpg"),
    sizes,
    alt,
    width: W,
    height: H,
    loading: eager ? "eager" : "lazy",
    decoding: eager ? "sync" : "async",
    fetchpriority: eager ? "high" : "auto",
    "data-testid": testid,
    onLoad: () => setLoaded(true),
    onError: () => setLoaded(true),
    style: {
      aspectRatio: String(ar),
      backgroundColor: bg,
      ...(fadeIn
        ? {
            opacity: loaded ? 1 : 0,
            filter: loaded ? "none" : "blur(8px)",
            transition: "opacity 700ms ease-out, filter 700ms ease-out",
          }
        : {}),
      ...imgStyle,
    },
    className,
    ...rest,
  };

  return (
    <picture className={pictureClassName}>
      <source type="image/webp" srcSet={srcSetFor(src, "webp")} sizes={sizes} />
      {onImg ? onImg(imgAttrs) : <img alt={alt} {...imgAttrs} />}
    </picture>
  );
}
